import { useTranslations } from 'next-intl'
import { CheckIcon } from 'primitives/icons'
import { useProductCountsQuery } from '@repo/web-ui/hooks/use-site-data'
import { Tooltip } from 'primitives/tooltip'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useHasAllAccessQuery } from '@repo/cf-user/hooks/use-user-data'

import { QuestionMarkIcon } from '@web/app/[locale]/clubhouse/assets/icons'
import { useClubhousesIsMemberQuery } from '@web/gen/clubhouses'

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num)
}

export function AllAccessBenefits({ clubhouseSlug }: { clubhouseSlug: string }) {
  const t = useTranslations('pages.clubhouse')
  const { data } = useProductCountsQuery()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: userHasAllAccess } = useHasAllAccessQuery()

  const { data: isMember } = useClubhousesIsMemberQuery(
    { input: { clubhouseSlug } },
    {
      select: data => data?.clubhousesIsClubhouseMember.isMember,
    },
  )

  return isLoggedIn && !userHasAllAccess && isMember ? (
    <section className="mt-5">
      <h2 className="text-14 font-semibold">{t('sidebar.all_access.what_is_included')}</h2>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <p className="text-14 text-cf-sf-gray-1050">
          {t('sidebar.all_access.all_premium_features')}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <p className="text-14 text-cf-sf-gray-1050">
          {formatNumber(data?.graphics || 0)} {t('sidebar.all_access.graphics')}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <p className="text-14 text-cf-sf-gray-1050">
          {formatNumber(data?.fonts || 0)} {t('sidebar.all_access.fonts')}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <p className="text-14 text-cf-sf-gray-1050">
          {formatNumber(data?.embroidery || 0)} {t('sidebar.all_access.embroidery')}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <div className="flex items-center gap-1">
          <p className="text-14 text-cf-sf-gray-1050">
            {formatNumber(data?.crafts || 0)} {t('sidebar.all_access.crafts')}
          </p>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                {t('sidebar.all_access.cut_files')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="size-3.5">
              <QuestionMarkIcon className="size-3.5 text-[#9CA3AF] " />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <div className="flex items-center gap-1">
          <p className="text-14 text-cf-sf-gray-1050">
            {' '}
            {t('sidebar.all_access.unlimited_flow_ai')}
          </p>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                {t('sidebar.all_access.unlimited_flow')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="size-3.5">
              <QuestionMarkIcon className="size-3.5 text-[#9CA3AF] " />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <div className="flex items-center gap-1">
          <p className="text-14 text-cf-sf-gray-1050">{t('sidebar.all_access.unlimited_studio')}</p>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                {t('sidebar.all_access.studio')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="size-3.5">
              <QuestionMarkIcon className="size-3.5 text-[#9CA3AF] " />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-start gap-2 pt-2">
        <CheckIcon className="size-5 text-[#3D424B]" />
        <div>
          <span className="text-14 text-cf-sf-gray-1050">
            {t('sidebar.all_access.license_all_assets')}
          </span>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                {t('sidebar.all_access.license')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="-mb-0.5 inline-block pl-1">
              <QuestionMarkIcon className="size-3.5 text-[#9CA3AF] " />
            </div>
          </Tooltip>
        </div>
      </div>
    </section>
  ) : null
}
